import React from 'react';
import { Clock, CheckCircle, AlertCircle, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Pricing() {
  const pricingPlans = [
    {
      title: 'Basic Service',
      price: '£84.95',
      duration: 'Up to 15 minutes labour',
      features: [
        'Includes call-out fee',
        'Fault diagnosis',
        'Repair service',
        'Fixed pricing'
      ],
      color: 'from-slate-600 to-slate-800'
    },
    {
      title: 'Standard Service',
      price: '£159.95',
      duration: 'Up to 30 minutes labour',
      features: [
        'Includes call-out fee',
        'Fault diagnosis',
        'Repair service',
        'Fixed pricing'
      ],
      color: 'from-slate-700 to-slate-900',
      popular: true
    },
    {
      title: 'Extended Service',
      price: '£229.95',
      duration: 'Up to 60 minutes labour',
      features: [
        'Includes call-out fee',
        'Fault diagnosis',
        'Repair service',
        'Fixed pricing'
      ],
      color: 'from-slate-600 to-slate-800'
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            <p className="text-white/60 font-medium mb-4 tracking-wide uppercase text-sm">Emergency Call-Out Pricing</p>
            <h1 className="text-4xl lg:text-5xl font-light text-white mb-4 leading-tight">
              Transparent
              <span className="block font-semibold">Fixed Pricing</span>
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We provide transparent, fixed pricing with no hidden costs, so you know exactly what you're paying before work begins.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className={`relative bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all ${
                  plan.popular ? 'ring-2 ring-slate-900' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-slate-900 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className={`p-8 rounded-t-3xl bg-gradient-to-br ${plan.color}`}>
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.title}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                  </div>
                  <p className="text-white/90 text-sm">{plan.duration}</p>
                </div>
                
                <div className="p-8">
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button 
                    className={`w-full py-3 px-6 rounded-xl font-medium transition-all ${
                      plan.popular 
                        ? 'bg-slate-900 hover:bg-slate-800 text-white' 
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                    }`}
                  >
                    Book Now
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Service Quality */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.4 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900">Professional Service</h3>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                All work is carried out by fully trained and experienced engineers, ensuring a fast and reliable service every time.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Certified professionals</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Fast response times</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Quality workmanship</span>
                </div>
              </div>
            </motion.div>

            {/* Payment & Additional Work */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900">Clear Terms</h3>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                If your issue requires more extensive or follow-on work, this will be clearly explained and agreed with you in advance—no unexpected charges.
              </p>
              <div className="bg-slate-50 rounded-xl p-6 border-l-4 border-slate-900">
                <div className="flex items-center gap-3 mb-3">
                  <CreditCard className="w-5 h-5 text-slate-900" />
                  <span className="font-semibold text-slate-900">Payment Required</span>
                </div>
                <p className="text-slate-600 text-sm">
                  Payment is required at the time of the booking to secure your emergency call-out slot.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            <h2 className="text-3xl lg:text-4xl font-light text-white mb-6">
              Need Emergency
              <span className="block font-semibold">Drainage Service?</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto mb-8">
              Contact us now for fast, reliable service with transparent pricing. Our team is ready to help you 24/7.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+12342312123" 
                className="bg-white hover:bg-slate-100 text-slate-900 px-8 py-4 font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <Clock className="w-5 h-5" />
                Call Now: (234) 231-2123
              </a>
              <a 
                href="/contact" 
                className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-4 font-medium rounded-xl transition-colors"
              >
                Contact Form
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
